/**
 * Lignes Excel export sinistres séjours — même structure que l’onglet Export Claims (AdminTripsClaims).
 * Réutilisé pour l’export activités afin de garantir le même ordre de colonnes.
 */

const formatWeatherParameter = (value) => {
    if (value == null || value === '') return '';
    if (Array.isArray(value)) return value.join(', ');
    return String(value);
};

const collectWeatherParametersFromSubcoverageTrips = (subcoverageTrips) => {
    if (!Array.isArray(subcoverageTrips) || subcoverageTrips.length === 0) return [];
    const params = subcoverageTrips.flatMap((sub) => {
        if (Array.isArray(sub?.weather_parameter)) return sub.weather_parameter;
        if (sub?.weather_parameter != null && sub.weather_parameter !== '') {
            return [sub.weather_parameter];
        }
        return [];
    });
    return [...new Set(params.map((p) => String(p).trim()).filter(Boolean))];
};

const resolveWeatherParameter = (block, cl) => {
    const pi = cl?.policy_info || block?.policy_info || {};
    const ri = cl?.reference_info || pi.reference_info || block?.reference_info || {};
    const ai = cl?.activity_info || pi.activity_info || block?.activity_info || {};
    const st = cl?.subcoverage_trip || pi.subcoverage_trip || block?.subcoverage_trip || {};

    const fromSubcoverageTrips = collectWeatherParametersFromSubcoverageTrips(ri.subcoverage_trips);
    if (fromSubcoverageTrips.length > 0) {
        return formatWeatherParameter(fromSubcoverageTrips);
    }

    return formatWeatherParameter(
        st.weather_parameter
            ?? cl?.weather_parameter
            ?? pi.weather_parameter
            ?? block?.weather_parameter
            ?? ai.weather_parameter
            ?? ri.weather_parameter
    );
};

const getFirstSubcoverageTrip = (block, cl) => {
    const pi = cl?.policy_info || block?.policy_info || {};
    const ri = cl?.reference_info || pi.reference_info || block?.reference_info || {};
    if (Array.isArray(ri.subcoverage_trips) && ri.subcoverage_trips.length > 0) {
        return ri.subcoverage_trips[0];
    }
    return cl?.subcoverage_trip || pi.subcoverage_trip || block?.subcoverage_trip || {};
};

const pickFirstDefined = (...values) => {
    for (const value of values) {
        if (value !== null && value !== undefined) {
            return value;
        }
    }
    return '';
};

const buildClaimTriggerExportFields = (block, cl) => {
    const checkClaims = cl?.CheckClaims || {};
    const subcoverageTrip = getFirstSubcoverageTrip(block, cl);

    return {
        is_triggered_forecast: pickFirstDefined(
            cl?.is_triggered_forecast,
            checkClaims.is_triggered_forecast
        ),
        count_hours_triggered: pickFirstDefined(cl?.count_hours_triggered, checkClaims.count_hours_triggered),
        temperature_is_triggered_forecast: pickFirstDefined(
            cl?.temperature_is_triggered_forecast,
            checkClaims.temperature_is_triggered_forecast
        ),
        wind_is_triggered_forecast: pickFirstDefined(
            cl?.wind_is_triggered_forecast,
            checkClaims.wind_is_triggered_forecast,
            subcoverageTrip.wind_is_triggered_forecast
        ),
        precipitation_is_triggered_forecast: pickFirstDefined(
            cl?.precipitation_is_triggered_forecast,
            checkClaims.precipitation_is_triggered_forecast,
            subcoverageTrip.precipitation_is_triggered_forecast
        ),
        prcp_trigger_hour_inf: pickFirstDefined(
            cl?.prcp_trigger_hour_inf,
            checkClaims.prcp_trigger_hour_inf,
            subcoverageTrip.prcp_trigger_hour_inf
        ),
        trigger_number_hours: pickFirstDefined(
            cl?.trigger_number_hours,
            checkClaims.trigger_number_hours,
            subcoverageTrip.trigger_number_hours
        ),
        temperature_trigger_hour_inf: pickFirstDefined(
            cl?.temperature_trigger_hour_inf,
            checkClaims.temperature_trigger_hour_inf,
            subcoverageTrip.temperature_trigger_hour_inf
        ),
        temperature_trigger_number_hours: pickFirstDefined(
            cl?.temperature_trigger_number_hours,
            checkClaims.temperature_trigger_number_hours,
            subcoverageTrip.temperature_trigger_number_hours
        ),
        wind_trigger_number_hours: pickFirstDefined(
            cl?.wind_trigger_number_hours,
            subcoverageTrip.wind_trigger_number_hours
        ),
        temperature_count_hours_triggered: pickFirstDefined(
            cl?.temperature_count_hours_triggered,
            checkClaims.temperature_count_hours_triggered
        ),
    };
};

const buildMergedActivityFields = (policyRowBase, cl, pi) => {
    const ai = cl.activity_info || {};
    return {
        reservation_number: ai.reservation_number ?? policyRowBase.reservation_number ?? '',
        activity_code: ai.activity_code ?? '',
        activity_start_date: ai.activity_start_date ?? policyRowBase.activity_start_date ?? '',
        activity_end_date: ai.activity_end_date ?? policyRowBase.activity_end_date ?? '',
        new_trip_start_date: ai.new_trip_start_date ?? policyRowBase.new_trip_start_date ?? '',
        new_trip_end_date: ai.new_trip_end_date ?? policyRowBase.new_trip_end_date ?? '',
        trip_effective_start_date: ai.trip_effective_start_date ?? policyRowBase.trip_effective_start_date ?? '',
        trip_effective_end_date: ai.trip_effective_end_date ?? policyRowBase.trip_effective_end_date ?? '',
        new_trip_effective_start_date: ai.new_trip_effective_start_date ?? policyRowBase.new_trip_effective_start_date ?? '',
        new_trip_effective_end_date: ai.new_trip_effective_end_date ?? policyRowBase.new_trip_effective_end_date ?? '',
        trip_price: ai.activity_price ?? policyRowBase.trip_price ?? '',
        new_trip_price: ai.new_trip_price ?? policyRowBase.new_trip_price ?? '',
        trip_modified: ai.trip_modified ?? policyRowBase.trip_modified ?? '',
        new_nb_nights_triggered: pi.new_nb_nights_triggered ?? policyRowBase.new_nb_nights_triggered ?? '',
        new_trip_number_nights: pi.new_trip_number_nights ?? policyRowBase.new_trip_number_nights ?? '',
    };
};

const buildPolicyOnlyFields = (policyRowBase) => {
    const {
        reservation_number: _reservationNumber,
        activity_start_date: _activityStartDate,
        activity_end_date: _activityEndDate,
        new_trip_start_date: _newTripStartDate,
        new_trip_end_date: _newTripEndDate,
        trip_effective_start_date: _tripEffectiveStartDate,
        trip_effective_end_date: _tripEffectiveEndDate,
        new_trip_effective_start_date: _newTripEffectiveStartDate,
        new_trip_effective_end_date: _newTripEffectiveEndDate,
        trip_price: _tripPrice,
        new_trip_price: _newTripPrice,
        trip_modified: _tripDateModified,
        ...policyOnly
    } = policyRowBase;
    return policyOnly;
};

function buildPolicyRowBaseFromTripBlock(block) {
    const pi = block.policy_info || {};
    const ai = pi.activity_info || block.activity_info || {};
    return {
        policy_code: pi.policy_code ?? '',
        reservation_number: ai.reservation_number ?? '',
        policy_status: pi.status ?? '',
        uw_date: pi.uw_date ?? '',
        nb_nights_triggered: pi.nb_nights_triggered ?? '',
        trip_number_nights: pi.trip_number_nights ?? '',
        new_nb_nights_triggered: pi.new_nb_nights_triggered ?? '',
        new_trip_number_nights: pi.new_trip_number_nights ?? '',
        activity_start_date: ai.activity_start_date ?? '',
        activity_end_date: ai.activity_end_date ?? '',
        new_trip_start_date: ai.new_trip_start_date ?? '',
        new_trip_end_date: ai.new_trip_end_date ?? '',
        trip_effective_start_date: ai.trip_effective_start_date ?? '',
        trip_effective_end_date: ai.trip_effective_end_date ?? '',
        new_trip_effective_start_date: ai.new_trip_effective_start_date ?? '',
        new_trip_effective_end_date: ai.new_trip_effective_end_date ?? '',
        trip_price: ai.activity_price ?? '',
        new_trip_price: ai.new_trip_price ?? '',
        trip_modified: ai.trip_modified ?? '',
        daily_sum_insured_maintain: pi.daily_sum_insured_maintain ?? '',
        daily_sum_insured_cancellation: pi.daily_sum_insured_cancellation ?? '',
        total_sum_insured_maintain: pi.total_sum_insured_maintain ?? '',
        total_sum_insured_cancellation: pi.total_sum_insured_cancellation ?? '',
        Total_claim: block.Total_claim ?? '',
        Total_claim_voucher: block.Total_claim_voucher ?? '',
        Total_claim_cash: block.Total_claim_cash ?? '',
        new_Total_claim: pickFirstDefined(
            block.New_Total_claim,
            block.new_Total_claim,
            block.new_total_claim
        ),
        new_Total_claim_voucher: pickFirstDefined(
            block.New_Total_claim_voucher,
            block.new_Total_claim_voucher,
            block.new_total_claim_voucher
        ),
        new_Total_claim_cash: pickFirstDefined(
            block.New_Total_claim_cash,
            block.new_Total_claim_cash,
            block.new_total_claim_cash
        ),
    };
}

export const TRIPS_CLAIMS_EXPORT_COLUMNS = [
    'claim_code',
    'policy_code',
    'client_code',
    'client_first_name',
    'client_last_name',
    'policy_status',
    'uw_date',
    'nb_nights_triggered',
    'trip_number_nights',
    'new_nb_nights_triggered',
    'new_trip_number_nights',
    'daily_sum_insured_maintain',
    'daily_sum_insured_cancellation',
    'total_sum_insured_maintain',
    'total_sum_insured_cancellation',
    'reservation_number',
    'activity_code',
    'activity_start_date',
    'activity_end_date',
    'new_trip_start_date',
    'new_trip_end_date',
    'trip_effective_start_date',
    'trip_effective_end_date',
    'new_trip_effective_start_date',
    'new_trip_effective_end_date',
    'trip_price',
    'new_trip_price',
    'trip_modified',
    'Total_claim',
    'Total_claim_voucher',
    'Total_claim_cash',
    'new_Total_claim',
    'new_Total_claim_voucher',
    'new_Total_claim_cash',
    'claim_id',
    'amount_of_claim',
    'new_amount_of_claim',
    'voucher_claim_amount_maintain',
    'voucher_claim_amount_cancellation',
    'cash_claim_amount_maintain',
    'cash_claim_amount_cancellation',
    'new_voucher_claim_amount_maintain',
    'new_voucher_claim_amount_cancellation',
    'new_cash_claim_amount_maintain',
    'new_cash_claim_amount_cancellation',
    'claim_status',
    'claim_stage',
    'date_of_claim',
    'payment_date',
    'trigger_based_on',
    'open_closed_claims',
    'activity_maintained',
    'weather_parameter',
    'is_triggered_forecast',
    'count_hours_triggered',
    'temperature_is_triggered_forecast',
    'wind_is_triggered_forecast',
    'precipitation_is_triggered_forecast',
    'prcp_trigger_hour_inf',
    'trigger_number_hours',
    'temperature_trigger_hour_inf',
    'temperature_trigger_number_hours',
    'wind_trigger_number_hours',
    'temperature_count_hours_triggered',
    'client_email',
    'account_holder_name',
    'partner_company_name',
    'note',
];

export const normalizeTripsClaimsExportRow = (row) => {
    const normalized = {};
    TRIPS_CLAIMS_EXPORT_COLUMNS.forEach((key) => {
        normalized[key] = row?.[key] ?? '';
    });
    return normalized;
};

export const normalizeTripsClaimsExportRows = (rows) => (
    Array.isArray(rows) ? rows.map(normalizeTripsClaimsExportRow) : []
);

export function buildTripsClaimsExportDataRow(policyRowBase, cl, block = null) {
    const ci = cl.client_info || {};
    const pr = cl.partner_info || {};
    const pi = cl.policy_info || block?.policy_info || {};
    const mergedActivity = buildMergedActivityFields(policyRowBase, cl, pi);

    return normalizeTripsClaimsExportRow({
        claim_code: cl.claim_code ?? '',
        policy_code: policyRowBase.policy_code ?? '',
        client_code: ci.client_code ?? '',
        client_first_name: ci.first_name ?? '',
        client_last_name: ci.last_name ?? '',
        ...buildPolicyOnlyFields(policyRowBase),
        claim_id: cl.claim_id ?? '',
        amount_of_claim: cl.amount_of_claim ?? '',
        new_amount_of_claim: cl.new_amount_of_claim ?? '',
        voucher_claim_amount_maintain: cl.voucher_claim_amount_maintain ?? '',
        voucher_claim_amount_cancellation: cl.voucher_claim_amount_cancellation ?? '',
        cash_claim_amount_maintain: cl.cash_claim_amount_maintain ?? '',
        cash_claim_amount_cancellation: cl.cash_claim_amount_cancellation ?? '',
        new_voucher_claim_amount_maintain: cl.new_voucher_claim_amount_maintain ?? '',
        new_voucher_claim_amount_cancellation: cl.new_voucher_claim_amount_cancellation ?? '',
        new_cash_claim_amount_maintain: cl.new_cash_claim_amount_maintain ?? '',
        new_cash_claim_amount_cancellation: cl.new_cash_claim_amount_cancellation ?? '',
        claim_status: cl.claim_status ?? '',
        claim_stage: cl.claim_stage ?? '',
        date_of_claim: cl.date_of_claim ?? '',
        payment_date: cl.payment_date ?? '',
        trigger_based_on: cl.trigger_based_on ?? '',
        open_closed_claims: cl.open_closed_claims ?? '',
        activity_maintained: cl.activity_maintained ?? '',
        ...mergedActivity,
        weather_parameter: resolveWeatherParameter(block, cl),
        ...buildClaimTriggerExportFields(block, cl),
        client_email: ci.email ?? '',
        account_holder_name: ci.account_holder_name ?? '',
        partner_company_name: pr.company_name ?? '',
    });
}

/**
 * @param {object} payload — ex. réponse GET api/trips/claims
 * @returns {object[]}
 */
export function flattenPoliciesWithClaimsForExcel(payload) {
    const list = payload?.policies_with_claims ?? [];
    const rows = [];
    list.forEach((block) => {
        const policyRowBase = buildPolicyRowBaseFromTripBlock(block);
        const claims = Array.isArray(block.claims) ? block.claims : [];
        if (claims.length === 0) {
            rows.push(normalizeTripsClaimsExportRow({
                claim_code: '',
                policy_code: policyRowBase.policy_code ?? '',
                client_code: '',
                client_first_name: '',
                client_last_name: '',
                ...buildPolicyOnlyFields(policyRowBase),
                ...buildMergedActivityFields(policyRowBase, {}, block.policy_info || {}),
                weather_parameter: resolveWeatherParameter(block, null),
                ...buildClaimTriggerExportFields(block, null),
                note: 'Police sans sinistre',
            }));
            return;
        }
        claims.forEach((cl) => {
            rows.push(buildTripsClaimsExportDataRow(policyRowBase, cl, block));
        });
    });
    return rows;
}

/**
 * Activités : GET /api/claims — même schéma Excel que les séjours si la réponse contient `policies_with_claims`
 * (non vide). Sinon liste plate : `claim_details` (réponse admin get_claims_with_details), `claims`, `data`, ou tableau racine.
 */
export function flattenActivityClaimsResponseForExcel(data) {
    const nested = data?.policies_with_claims;
    if (Array.isArray(nested) && nested.length > 0) {
        return flattenPoliciesWithClaimsForExcel(data);
    }

    let raw = [];
    if (Array.isArray(data?.claim_details)) {
        raw = data.claim_details;
    } else if (Array.isArray(data?.data?.claim_details)) {
        raw = data.data.claim_details;
    } else if (Array.isArray(data?.claims)) {
        raw = data.claims;
    } else if (Array.isArray(data?.data)) {
        raw = data.data;
    } else if (Array.isArray(data)) {
        raw = data;
    }
    if (!raw.length) {
        return [];
    }
    return raw.map((cl) => {
        const pi = cl.policy_info || {};
        const policyRowBase = {
            policy_code: pi.policy_code ?? '',
            policy_status: pi.status ?? '',
            uw_date: pi.uw_date ?? '',
            nb_nights_triggered: pi.nb_nights_triggered ?? '',
            trip_number_nights: pi.trip_number_nights ?? '',
            new_nb_nights_triggered: pi.new_nb_nights_triggered ?? '',
            new_trip_number_nights: pi.new_trip_number_nights ?? '',
            daily_sum_insured_maintain: pi.daily_sum_insured_maintain ?? '',
            daily_sum_insured_cancellation: pi.daily_sum_insured_cancellation ?? '',
            total_sum_insured_maintain: pi.total_sum_insured_maintain ?? '',
            total_sum_insured_cancellation: pi.total_sum_insured_cancellation ?? '',
            Total_claim: '',
            Total_claim_voucher: '',
            Total_claim_cash: '',
            new_Total_claim: '',
            new_Total_claim_voucher: '',
            new_Total_claim_cash: '',
        };
        return buildTripsClaimsExportDataRow(policyRowBase, cl);
    });
}
